import { getVariationPrice } from '@util/firestore/products';
import { CartItem } from '@util/types/firestore/carts';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { ProductDocument, Variation } from 'models/product';
import { useMemo, useState, useEffect, useCallback } from 'react';

const useBuyerActionButtons = (product: ProductDocument) => {
  const [selectedVariations, setSelectedVariations] = useState<
    Variation[] | null
  >(null);
  const [errors, setErrors] = useState<string[]>([]);
  const { cart, updateCartItem } = useShoppingCart();

  const cartItem = useMemo(
    () => cart?.items.find((c) => c.product_id === product.id),
    [cart, product.id]
  );

  const onSelection = (v: Variation, isNumber: boolean, isColor: boolean) => {
    const prev = selectedVariations || [];
    let newSelectedVariations: Variation[];

    if (isColor) {
      const existingColorIndex = prev.findIndex(
        (variation) => !!variation.color
      );

      if (existingColorIndex !== -1) {
        newSelectedVariations = [...prev];
        newSelectedVariations[existingColorIndex] = v;
      } else {
        newSelectedVariations = [v, ...prev];
      }
    } else if (isNumber) {
      const existingNumberIndex = prev.findIndex(
        (variation) => variation.is_number
      );

      if (existingNumberIndex !== -1) {
        newSelectedVariations = [...prev];
        newSelectedVariations[existingNumberIndex] = v;
      } else {
        newSelectedVariations = [v, ...prev];
      }
    } else {
      const existingLetterIndex = prev.findIndex(
        (variation) => !variation.is_number && !variation.color
      );

      if (existingLetterIndex !== -1) {
        newSelectedVariations = [...prev];
        newSelectedVariations[existingLetterIndex] = v;
      } else {
        newSelectedVariations = [v, ...prev];
      }
    }

    setSelectedVariations(newSelectedVariations);
    setErrors((prev) =>
      prev.filter((e) => e !== (isNumber ? 'Pant' : 'Jersey'))
    );

    if (cartItem && newSelectedVariations.length >= 1) {
      const letter = newSelectedVariations.find(
        (v) => v.size && isNaN(+v.size) && !v.is_number
      )?.size as string;
      const number = newSelectedVariations.find(
        (v) => v.size && !isNaN(+v.size) && v.is_number
      )?.size as number;
      const color = newSelectedVariations.find(
        (v) => v.color && v.color !== 'N/A'
      )?.color as string;
      updateCartItem({
        ...cartItem,
        product_cost: getVariationPrice(product, newSelectedVariations),
        size: {
          ...(letter && { letter }),
          ...(number && { number }),
        },
        color: color || '',
      });
    }
  };

  const getInitialVariations = useCallback(
    (
      product: ProductDocument,
      cartItem?: CartItem,
      params?: URLSearchParams
    ) => {
      const variations = [];
      // if the cart item has a letter but no size, return the matching variation in an array
      if (cartItem?.size?.letter) {
        const tmp = product.variations!.find(
          (v) => v.size === cartItem.size!.letter
        );
        if (tmp) {
          variations.push(tmp);
        }
      }
      // if the cart item has a size but no letter, return the matching variation in an array
      if (cartItem?.size?.number) {
        const tmp = product.variations!.find(
          (v) => v.size === cartItem.size!.number
        );
        if (tmp) {
          variations.push(tmp);
        }
      }
      if (cartItem?.color) {
        const tmp = product.variations!.find((v) => v.color === cartItem.color);
        if (tmp) {
          variations.push(tmp);
        }
      }
      if (variations.length) {
        return variations.filter((v) => v) as Variation[];
      }

      // if the cart item has no size or letter, check the query string
      // if the query string has a size, return the matching variation in an array
      if (params?.has('size')) {
        const size = params.get('size');
        if (size) {
          const sizes = size.split(',');
          const numberSize = sizes.find((s) => !isNaN(+s));
          const letterSize = sizes.find((s) => isNaN(+s));
          // if the cart item has a letter and size, return the matching variations in an array
          if (letterSize && numberSize) {
            return [
              product.variations!.find((v) => v.size === letterSize),
              product.variations!.find((v) => v.size === +numberSize),
            ].filter((v) => v) as Variation[];
          }
          // if the cart item has a letter but no size, return the matching variation in an array
          else if (letterSize) {
            return [
              product.variations!.find((v) => v.size === letterSize),
            ].filter((v) => v) as Variation[];
          }
          // if the cart item has a size but no letter, return the matching variation in an array
          else if (numberSize) {
            return [
              product.variations!.find((v) => v.size === +numberSize),
            ].filter((v) => v) as Variation[];
          }
        }
      }

      // auto select the first variation in stock - but only select one of each type
      const result: Variation[] = [];

      // Only select one letter size variation (non-number, non-color)
      const defaultLetterVariation = product.variations?.find(
        (v) =>
          v.qty > 0 &&
          isNaN(+v.size) &&
          (!v.color || v.color === 'N/A') &&
          !v.is_number
      );
      if (defaultLetterVariation) {
        result.push(defaultLetterVariation);
      }

      // Only select one number size variation
      const defaultNumberVariation = product.variations?.find(
        (v) =>
          v.qty > 0 &&
          !isNaN(+v.size) &&
          v.is_number &&
          (!v.color || v.color === 'N/A')
      );
      if (defaultNumberVariation) {
        result.push(defaultNumberVariation);
      }

      // Only select one color variation (but ensure we don't duplicate if it already has size)
      const defaultColorVariation = product.variations?.find(
        (v) =>
          v.qty > 0 &&
          v.color &&
          v.color !== 'N/A' &&
          !result.some(
            (existing) => existing.color === v.color && existing.size === v.size
          )
      );
      if (defaultColorVariation) {
        result.push(defaultColorVariation);
      }

      return result;
    },
    []
  );

  useEffect(() => {
    if (!selectedVariations?.length && product.has_variations && cartItem) {
      setSelectedVariations(getInitialVariations(product, cartItem));
    }
  }, [product, getInitialVariations, cartItem, selectedVariations?.length]);

  return {
    selectedVariations,
    setSelectedVariations,
    errors,
    setErrors,
    cartItem,
    updateCartItem,
    onSelection,
  };
};

export default useBuyerActionButtons;
